import { AlertCircle, CheckCircle2, Loader2, RefreshCw, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import type { UpdateStatusPayload } from '../../../main/preload';

function BottomIndicator({
  status,
  onDismiss,
}: {
  status: UpdateStatusPayload;
  onDismiss?: () => void;
}) {
  const { status: s, percent } = status;

  let icon: React.ReactNode;
  let text: string;
  let showDismiss = false;

  if (s === 'checking') {
    icon = <Loader2 size={13} className="animate-spin text-pos-primary" />;
    text = 'Checking for updates…';
  } else if (s === 'available') {
    icon = <Loader2 size={13} className="animate-spin text-pos-primary" />;
    text = 'Update available, downloading…';
  } else if (s === 'downloading') {
    icon = <Loader2 size={13} className="animate-spin text-pos-primary" />;
    text = `Downloading update… ${percent ?? 0}%`;
  } else if (s === 'not-available') {
    icon = <CheckCircle2 size={13} className="text-green-400" />;
    text = 'Already up to date';
  } else {
    icon = <AlertCircle size={13} className="text-red-400" />;
    text = 'Update check failed';
    showDismiss = true;
  }

  return (
    <div className="fixed bottom-4 left-4 z-[9999] flex items-center gap-2 rounded-full bg-pos-dark/90 px-3 py-1.5 text-xs font-medium text-white shadow-lg backdrop-blur-sm">
      {icon}
      <span>{text}</span>
      {showDismiss && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="ml-0.5 rounded-full p-0.5 opacity-70 hover:opacity-100 focus:outline-none"
        >
          <X size={11} />
        </button>
      )}
    </div>
  );
}

export function UpdateBanner() {
  const [status, setStatus] = useState<UpdateStatusPayload | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    if (!window.electron?.updater) return;
    const unsubscribe = window.electron.updater.onStatusChange((payload) => {
      setStatus(payload);
      setDismissed(false);
      setIsInstalling(false);
    });
    // eslint-disable-next-line consistent-return
    return () => {
      unsubscribe();
    };
  }, []);

  // Auto-dismiss transient terminal states
  useEffect(() => {
    if (
      !status ||
      (status.status !== 'not-available' && status.status !== 'error')
    )
      return;
    const delay = status.status === 'not-available' ? 3000 : 4000;
    const timer = setTimeout(() => setDismissed(true), delay);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [status]);

  if (dismissed || !status) return null;

  // Prominent top banner when update is ready to install
  if (status.status === 'downloaded') {
    const handleInstall = () => {
      setIsInstalling(true);
      window.electron.updater.installUpdate();
    };

    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between gap-3 border-b-2 border-pos-primary bg-pos-dark px-4 py-2 shadow-lg">
        <div className="flex items-center gap-2 text-sm font-medium text-white">
          <RefreshCw size={15} className="text-pos-primary" />
          <span>A new update is ready to install.</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleInstall}
            disabled={isInstalling}
            className="flex items-center gap-1.5 rounded bg-pos-primary px-3 py-1 text-xs font-semibold text-white hover:bg-pos-secondary focus:outline-none focus:ring-2 focus:ring-pos-primary disabled:opacity-70"
          >
            {isInstalling ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Relaunching…
              </>
            ) : (
              'Relaunch to update'
            )}
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="rounded p-0.5 text-pos-light opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-pos-primary"
          >
            <X size={15} />
          </button>
        </div>
      </div>
    );
  }

  // Small bottom-left indicator for all other active states
  const showIndicator =
    status.status === 'checking' ||
    status.status === 'available' ||
    status.status === 'downloading' ||
    status.status === 'not-available' ||
    status.status === 'error';

  if (!showIndicator) return null;

  return (
    <BottomIndicator status={status} onDismiss={() => setDismissed(true)} />
  );
}
